import React, { useEffect, useState } from 'react';
import { ScrollView, TextInput, TouchableOpacity, View } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Toggle from '../../../../components/Toggle';
import Icon from '../../../../components/IcomoonIcon';
import Header from '../../../../navigation/Header';
import useCommunitySettings from '../../../../hooks/useCommunitySettings';


export const QR_CATEGORIES = {
  personal: { title: 'Personal',  subtitle: 'Amigos y familiares',        configTitle: 'Configuración QR Personal' },
  provider: { title: 'Proveedor', subtitle: 'Servicios del hogar',        configTitle: 'Configuración QR Proveedores' },
  meeting:  { title: 'Reunión',   subtitle: 'Varios invitados',           configTitle: 'Configuración QR Reunión' },
  delivery: { title: 'Entrega',   subtitle: 'Paquetería y repartidores',  configTitle: 'Configuración QR Entrega' },
} as const;

export type QRCategoryKey = keyof typeof QR_CATEGORIES;


/** Tarjeta blanca con un toggle, título, descripción y contenido opcional (sub-ajustes). */
function SettingCard({ title, description, value, onValueChange, disabled, highlight, children }: any) {
  return (
    <View style={tw.style(`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`, highlight && `border-primary`)}>
      <View style={tw`row items-center justify-between`}>
        <Text style={tw`text-secondary font-600 text-base flex-1 mr-3`}>{title}</Text>
        <Toggle value={value} onValueChange={onValueChange} disabled={disabled} />
      </View>
      {!!description && <Text style={tw`text-gray-500 text-sm mt-2`}>{description}</Text>}
      {children}
    </View>
  )
}

/** Sub-ajuste (dentro de una tarjeta), con toggle más pequeño. */
function SubToggleRow({ title, description, value, onValueChange, disabled }: any) {
  return (
    <View style={tw`py-3 border-t border-gray-100`}>
      <View style={tw`row items-center justify-between`}>
        <Text style={tw`text-secondary font-500 flex-1 mr-3`}>{title}</Text>
        <Toggle size='sm' value={value} onValueChange={onValueChange} disabled={disabled} />
      </View>
      {!!description && <Text style={tw`text-gray-500 text-sm mt-1`}>{description}</Text>}
    </View>
  )
}


/** Campo numérico con estado local: se persiste al terminar de editar (no en cada tecla). */
function NumberField({ value, onChange, disabled }: { value: number; onChange: (n: number) => void; disabled?: boolean }) {
  const [local, setLocal] = useState(`${value}`)
  useEffect(() => { setLocal(`${value}`) }, [value])

  const commit = () => onChange(parseInt(local || '0', 10) || 0)

  return (
    <TextInput
      value={local}
      onChangeText={setLocal}
      onEndEditing={commit}
      onBlur={commit}
      keyboardType='numeric'
      editable={!disabled}
      style={tw`bg-gray-100 rounded-lg px-4 h-10 w-24 text-right text-secondary font-600`}
    />
  )
}


const DEFAULT_PROVIDER_SERVICES = ['Jardinería', 'Mascotas', 'Gas', 'Agua', 'Limpieza'];


/** Contenido de configuración de una categoría. Se usa tanto en la pantalla
 *  nativa como (a futuro) en el panel de columnas en web. */
export function QRCategoryConfig({ category }: { category: QRCategoryKey }) {
  const { updateSettings, getSettingValue, isUpdating } = useCommunitySettings()

  const allowedTypes = getSettingValue('guest_passes.allowed_types', ['personal', 'meeting']) as string[]
  const enabled = allowedTypes.includes(category)

  const toggleEnabled = (value: boolean) => {
    const next = value
      ? [...new Set([...allowedTypes, category])]
      : allowedTypes.filter(t => t !== category)
    updateSettings('guest_passes.allowed_types', next)
  }

  // Helpers de settings por categoría
  const k = (field: string) => `guest_passes.${category}.${field}`
  const get = (field: string, def: any) => getSettingValue(k(field), def)
  const set = (field: string, value: any) => updateSettings(k(field), value)

  const enableCard = (title: string, description: string) => (
    <SettingCard
      title={title}
      description={description}
      value={enabled}
      onValueChange={toggleEnabled}
      disabled={isUpdating}
      highlight
    />
  )

  const accesoAbiertoCard = (description: string) => (
    <SettingCard
      title="Acceso abierto"
      description={description}
      value={get('open_access', false)}
      onValueChange={(v: boolean) => set('open_access', v)}
      disabled={isUpdating}
    />
  )

  if (category === 'personal') {
    const vehicleRequired = getSettingValue('guest_passes.guest_vehicle_required', false)
    return (
      <>
        {enableCard('Habilitar código Personal', 'Amigos o familiares cercanos. Los residentes pueden generar y mantener varios códigos activos a la vez.')}

        <SettingCard
          title="Códigos permanentes"
          description="Los residentes podrán crear códigos que no tienen una fecha de caducidad."
          value={getSettingValue('guest_passes.allow_permanent', false)}
          onValueChange={(v: boolean) => updateSettings('guest_passes.allow_permanent', v)}
          disabled={isUpdating}
        />

        <SettingCard
          title="Vehículo obligatorio"
          description="Para poder generar los códigos, será necesario brindar la información del vehículo que accesa:"
          value={vehicleRequired}
          onValueChange={(v: boolean) => updateSettings('guest_passes.guest_vehicle_required', v)}
          disabled={isUpdating}
        >
          {vehicleRequired && (
            <View style={tw`mt-2`}>
              <SubToggleRow
                title="Marca y modelo"
                description="Se necesitará proporcionar dos palabras de al menos dos caracteres cada una."
                value={getSettingValue('guest_passes.vehicle_info.brand_required', true)}
                onValueChange={(v: boolean) => updateSettings('guest_passes.vehicle_info.brand_required', v)}
                disabled={isUpdating}
              />
              <SubToggleRow
                title="Matrícula"
                description="6 caracteres entre números y letras."
                value={getSettingValue('guest_passes.vehicle_info.plate_required', true)}
                onValueChange={(v: boolean) => updateSettings('guest_passes.vehicle_info.plate_required', v)}
                disabled={isUpdating}
              />
              <SubToggleRow
                title="Color del vehículo"
                value={getSettingValue('guest_passes.vehicle_info.color_required', true)}
                onValueChange={(v: boolean) => updateSettings('guest_passes.vehicle_info.color_required', v)}
                disabled={isUpdating}
              />
            </View>
          )}
        </SettingCard>

        {accesoAbiertoCard('Permite que los residentes elijan si sus invitados pueden entrar y salir varias veces en el periodo seleccionado.')}
      </>
    )
  }

  if (category === 'provider') {
    return (
      <>
        {enableCard('Habilitar código Proveedores', 'Permite el acceso a trabajadores a la propiedad.')}

        <SettingCard
          title="Nombre del responsable"
          description="Los residentes deberán escribir el nombre del proveedor que les visita."
          value={get('responsible_name_required', false)}
          onValueChange={(v: boolean) => set('responsible_name_required', v)}
          disabled={isUpdating}
        />

        <SettingCard
          title="Detalles obligatorios"
          description="Se debe dar una descripción del servicio que se brindará al domicilio."
          value={get('details_required', false)}
          onValueChange={(v: boolean) => set('details_required', v)}
          disabled={isUpdating}
        />

        <ProviderServicesCard
          services={get('predefined_services', DEFAULT_PROVIDER_SERVICES) as string[]}
          onChange={(next) => set('predefined_services', next)}
          disabled={isUpdating}
        />

        {accesoAbiertoCard('Permite que el residente decida si un proveedor puede reingresar con el mismo código.')}
      </>
    )
  }

  if (category === 'meeting') {
    const limit = get('limit_guests', false)
    return (
      <>
        {enableCard('Habilitar código Reunión', 'Lista de invitados válido por un día. Se generan códigos únicos para cada invitado. Solo pueden tener un código de reunión activo a la vez.')}

        <SettingCard
          title="Limitar invitados"
          description="Limita la cantidad de personas que se pueden agregar en la invitación."
          value={limit}
          onValueChange={(v: boolean) => set('limit_guests', v)}
          disabled={isUpdating}
        >
          {limit && (
            <View style={tw`row items-center justify-between border-t border-gray-100 pt-3 mt-2`}>
              <Text style={tw`text-secondary font-500`}>Cantidad</Text>
              <NumberField
                value={get('max_guests', 10)}
                onChange={(n) => set('max_guests', n)}
                disabled={isUpdating}
              />
            </View>
          )}
        </SettingCard>

        {accesoAbiertoCard('Permite que las visitas puedan ingresar y salir varias veces durante el día de la reunión con el código generado.')}
      </>
    )
  }

  // delivery
  return (
    <>
      {enableCard('Habilitar código Entrega', 'Servicios de mensajería y repartidores de entrada por salida.')}

      <SettingCard
        title="Nombre obligatorio"
        description="Los residentes deberán proporcionar el nombre de la empresa de repartos que visitará el domicilio."
        value={get('name_required', false)}
        onValueChange={(v: boolean) => set('name_required', v)}
        disabled={isUpdating}
      />

      <SettingCard
        title="Entrega en caseta"
        description="Permite que el reparto pueda ser dejado en caseta para evitar el ingreso del repartidor."
        value={get('leave_at_booth', false)}
        onValueChange={(v: boolean) => set('leave_at_booth', v)}
        disabled={isUpdating}
      />
    </>
  )
}


/** Tarjeta de "Servicios predefinidos" (checkboxes + agregar nuevo). */
function ProviderServicesCard({ services, onChange, disabled }: { services: string[]; onChange: (s: string[]) => void; disabled?: boolean }) {
  const [newService, setNewService] = useState('')

  // Estado optimista local: la selección cambia al instante y se sincroniza con el servidor.
  const [local, setLocal] = useState<string[]>(services)
  useEffect(() => { setLocal(services) }, [services])

  // Lista visible = predefinidos base + los que ya estén guardados
  const options = [...new Set([...DEFAULT_PROVIDER_SERVICES, ...local])]

  const toggle = (name: string) => {
    if (disabled) return
    const next = local.includes(name) ? local.filter(s => s !== name) : [...local, name]
    setLocal(next)
    onChange(next)
  }

  const add = () => {
    const name = newService.trim()
    if (!name) return
    const next = [...new Set([...local, name])]
    setLocal(next)
    onChange(next)
    setNewService('')
  }

  return (
    <View style={tw`bg-white rounded-2xl border border-gray-200 px-4 py-4 mb-3`}>
      <Text style={tw`text-secondary font-600 text-base`}>Servicios predefinidos</Text>
      <Text style={tw`text-gray-500 text-sm mt-1 mb-3`}>
        Crea accesos rápidos para servicios (Gas, Agua, etc.) y agiliza la creación de pases:
      </Text>

      {options.map(name => {
        const checked = local.includes(name)
        return (
          <TouchableOpacity
            key={name}
            style={tw`row items-center justify-between py-3 border-t border-gray-100`}
            onPress={() => toggle(name)}
          >
            <Text style={tw`text-secondary`}>{name}</Text>
            <View style={tw.style(`w-6 h-6 rounded-md border items-center justify-center`, checked ? `bg-primary border-primary` : `border-gray-300`)}>
              {checked && <Icon name='check' size={14} color={tw.color('white')} />}
            </View>
          </TouchableOpacity>
        )
      })}

      <View style={tw`bg-gray-50 rounded-xl p-3 mt-3`}>
        <Text style={tw`text-center text-secondary font-600 mb-2`}>Agregar servicios</Text>
        <TextInput
          value={newService}
          onChangeText={setNewService}
          placeholder='Nombre del servicio'
          placeholderTextColor={tw.color('gray-400')}
          style={tw`bg-white rounded-full px-4 h-11 text-center text-secondary mb-2`}
        />
        <TouchableOpacity style={tw`bg-primary rounded-full h-11 items-center justify-center`} onPress={add}>
          <Text style={tw`text-white font-600`}>Agregar</Text>
        </TouchableOpacity>
      </View>
    </View>
  )
}


export default function QRCategoryConfigScreen({ route }: any) {
  const category: QRCategoryKey = route?.params?.category ?? 'personal'
  const meta = QR_CATEGORIES[category]

  return (
    <View style={tw`flex-1 bg-[#F3F2F3]`}>
      <Header showBackButton title={meta.configTitle} style="bg-transparent" />

      <ScrollView style={tw`flex-1`} contentContainerStyle={tw`px-4 pt-4 pb-10`}>
        <QRCategoryConfig category={category} />
      </ScrollView>
    </View>
  );
}
